import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow access to waiting-for-invite page even without organization
  const isWaitingPage = location.pathname === '/waiting-for-invite';
  const isOrgSetupPage = location.pathname === '/organization/setup';
  
  // Super admins can access everything
  const isSuperAdmin = user?.isSuperAdmin;
  
  // For role-protected routes (admin/employee dashboards), require organization membership
  // unless the user is a super admin
  if (requiredRole && !isSuperAdmin) {
    // Check role match first
    if (user?.role !== requiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
    
    // If user has no organization, redirect to waiting page
    if (!user?.organizationId && !isWaitingPage && !isOrgSetupPage) {
      return <Navigate to="/waiting-for-invite" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
